import React, { useState } from 'react';
import { X, Loader2, Terminal, ArrowRight, ArrowLeft } from 'lucide-react';
interface WaitlistFormProps {
  isOpen: boolean;
  onClose: () => void;
}
export const WaitlistForm = ({
  isOpen,
  onClose
}: WaitlistFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: 'individual',
    companyName: '',
    operatingSystem: [] as string[],
    jobRole: '',
    industry: '',
    package: '',
    message: ''
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const steps = [{
    field: 'name',
    label: "What's your name?",
    type: 'text',
    required: true,
    placeholder: 'Enter your full name'
  }, {
    field: 'email',
    label: "What's your email?",
    type: 'email',
    required: true,
    placeholder: 'Enter your email'
  }, {
    field: 'userType',
    label: 'Are you an individual or company?',
    type: 'select',
    options: [{
      value: 'individual',
      label: 'Individual'
    }, {
      value: 'company',
      label: 'Company'
    }]
  }, {
    field: 'operatingSystem',
    label: 'Which operating systems do you use?',
    type: 'checkbox',
    options: [{
      value: 'windows',
      label: 'Windows'
    }, {
      value: 'macos',
      label: 'MacOS'
    }]
  }, {
    field: 'jobRole',
    label: "What's your job role?",
    type: 'text',
    placeholder: 'Enter your job role'
  }, {
    field: 'industry',
    label: 'What industry do you work in?',
    type: 'text',
    placeholder: 'Enter your industry'
  }, {
    field: 'package',
    label: 'Which package are you interested in? (Optional)',
    type: 'select',
    options: [{
      value: '',
      label: 'Select a package (optional)'
    }, {
      value: 'individual',
      label: 'Individual/Small Business - $9.99/user/month'
    }, {
      value: 'medium',
      label: 'Medium Business - $14.99/user/month'
    }, {
      value: 'enterprise',
      label: 'Enterprise - $19.99/user/month'
    }]
  }, {
    field: 'message',
    label: 'Any additional information? (Optional)',
    type: 'textarea',
    placeholder: 'Tell us more about your needs or ask any questions...'
  }];
  // Add company name step if userType is company
  if (formData.userType === 'company') {
    steps.splice(3, 0, {
      field: 'companyName',
      label: "What's your company name?",
      type: 'text',
      required: true,
      placeholder: 'Enter company name'
    });
  }
  const handleNext = () => {
    const currentField = steps[currentStep].field;
    if (steps[currentStep].required && !formData[currentField as keyof typeof formData]) {
      setError('This field is required');
      return;
    }
    setError('');
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handlePrevious = () => {
    setError('');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://formspree.io/f/xovwpwbd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          onClose();
          setFormData({
            name: '',
            email: '',
            userType: 'individual',
            companyName: '',
            operatingSystem: [],
            jobRole: '',
            industry: '',
            package: '',
            message: ''
          });
          setCurrentStep(0);
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setError('There was an error submitting the form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  if (!isOpen) return null;
  const currentField = steps[currentStep];
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" data-id="element-318">
      <div className="bg-white rounded-xl w-full max-w-lg p-8 relative shadow-2xl" data-id="element-319">
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors duration-200" aria-label="Close form" data-id="element-320">
          <X className="h-6 w-6" data-id="element-321" />
        </button>

        {submitted ? <div className="text-center py-12" data-id="element-322">
            <div className="flex items-center justify-center mb-6" data-id="element-323">
              <Terminal className="h-12 w-12 text-indigo-600" data-id="element-324" />
              <span className="ml-2 text-2xl font-bold text-gray-900" data-id="element-325">
                TechAIgent
              </span>
            </div>
            <h3 className="text-2xl font-bold mb-2 text-gray-900" data-id="element-326">
              Thank You!
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-327">
              We've received your information and will be in touch soon.
            </p>
            <button onClick={onClose} className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium" data-id="element-328">
              Close
            </button>
          </div> : <>
            <div className="text-center mb-8" data-id="element-329">
              <div className="flex items-center justify-center mb-4" data-id="element-330">
                <Terminal className="h-12 w-12 text-indigo-600" data-id="element-331" />
                <span className="ml-2 text-3xl font-bold text-gray-900" data-id="element-332">
                  TechAIgent
                </span>
              </div>
              <div className="h-1 w-full bg-gray-100 rounded-full mb-6" data-id="element-333">
                <div className="h-1 bg-indigo-600 rounded-full transition-all duration-300" style={{
              width: `${(currentStep + 1) / steps.length * 100}%`
            }} data-id="element-334" />
              </div>
            </div>

            <form onSubmit={e => e.preventDefault()} className="space-y-6" data-id="element-335">
              <div className="min-h-[200px]" data-id="element-336">
                <label className="block text-xl font-medium text-gray-900 mb-4" data-id="element-337">
                  {currentField.label}
                </label>

                {currentField.type === 'select' && <select value={formData[currentField.field as keyof typeof formData] as string} onChange={e => setFormData({
              ...formData,
              [currentField.field]: e.target.value
            })} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" data-id="element-338">
                    {currentField.options?.map(option => <option key={option.value} value={option.value} data-id="element-339">
                        {option.label}
                      </option>)}
                  </select>}

                {currentField.type === 'checkbox' && <div className="space-y-3" data-id="element-340">
                    {currentField.options?.map(option => <label key={option.value} className="flex items-center" data-id="element-341">
                        <input type="checkbox" value={option.value} checked={formData.operatingSystem.includes(option.value)} onChange={e => {
                  const updatedOS = e.target.checked ? [...formData.operatingSystem, e.target.value] : formData.operatingSystem.filter(os => os !== e.target.value);
                  setFormData({
                    ...formData,
                    operatingSystem: updatedOS
                  });
                }} className="w-5 h-5 mr-3 text-indigo-600 focus:ring-indigo-500 rounded" data-id="element-342" />
                        <span className="text-lg text-gray-700" data-id="element-343">
                          {option.label}
                        </span>
                      </label>)}
                  </div>}

                {currentField.type === 'textarea' && <textarea value={formData[currentField.field as keyof typeof formData] as string} onChange={e => setFormData({
              ...formData,
              [currentField.field]: e.target.value
            })} rows={5} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-500 text-lg resize-none" placeholder={currentField.placeholder} data-id="element-344" />}

                {(currentField.type === 'text' || currentField.type === 'email') && <input type={currentField.type} required={currentField.required} value={formData[currentField.field as keyof typeof formData] as string} onChange={e => setFormData({
              ...formData,
              [currentField.field]: e.target.value
            })} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-500 text-lg" placeholder={currentField.placeholder} data-id="element-345" />}

                {error && <p className="mt-2 text-sm text-red-600" data-id="element-346">{error}</p>}
              </div>

              <div className="flex justify-between items-center pt-6" data-id="element-347">
                <button type="button" onClick={handlePrevious} className={`flex items-center px-4 py-2 rounded-lg font-medium transition ${currentStep === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-gray-900'}`} disabled={currentStep === 0} data-id="element-348">
                  <ArrowLeft className="h-5 w-5 mr-2" data-id="element-349" />
                  Back
                </button>

                {currentStep === steps.length - 1 ? <button type="button" onClick={handleSubmit} disabled={isLoading} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition flex items-center" data-id="element-350">
                    {isLoading ? <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" data-id="element-351" />
                        Submitting...
                      </> : 'Submit'}
                  </button> : <button type="button" onClick={handleNext} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition flex items-center" data-id="element-352">
                    Next
                    <ArrowRight className="h-5 w-5 ml-2" data-id="element-353" />
                  </button>}
              </div>
            </form>
          </>}
      </div>
    </div>;
};